/**
 * Bidirectional mapping between FHIR Patient and Client Profile form state.
 *
 * Reads/writes Patient.name, telecom, address, birthDate, communication,
 * and Unify-specific extensions (gender identity, LGBTQIA, religion,
 * race/ethnicity) per PRD_CLIENTS_FHIR_MAPPING.
 *
 * Important: patch helpers preserve unknown extensions and unrelated fields.
 */

import type {
  Address,
  CodeableConcept,
  Coding,
  ContactPoint,
  Extension,
  HumanName,
  Patient,
  PatientCommunication,
  PatientContact,
  RelatedPerson,
} from '@medplum/fhirtypes';
import {
  PATIENT_EXT,
  EMERGENCY_CONTACT_RELATIONSHIP_SYSTEM,
  EMERGENCY_CONTACT_CODE,
  V3_CODE_TO_RELATIONSHIP_LABEL,
  relationshipCodingForType,
} from '@/lib/fhirValueSets/clientUserProfile';
import {
  CONTACT_RELATIONSHIP_TYPES,
  type Contact,
  type ContactKind,
  type ContactPhone,
  type ContactRelationshipType,
  type ContactSource,
} from '../data/contactTypes';

// ---------------------------------------------------------------------------
// Profile form state
// ---------------------------------------------------------------------------

export interface ClientProfileFormState {
  preferredFirstName: string;
  preferredLastName: string;
  legalFirstName: string;
  legalLastName: string;
  dateOfBirth: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  genderIdentity: string;
  lgbtqia: string[];
  religion: string[];
  raceEthnicity: string[];
  languages: string[];
}

// ---------------------------------------------------------------------------
// Patient → form
// ---------------------------------------------------------------------------

function findName(patient: Patient, use: string): HumanName | undefined {
  return patient.name?.find((n) => n.use === use);
}

function findTelecom(
  patient: Patient,
  system: string,
): ContactPoint | undefined {
  return patient.telecom?.find((t) => t.system === system);
}

function getStringListExtension(
  patient: Patient,
  url: string,
): string[] {
  const ext = patient.extension?.find((e) => e.url === url);
  if (!ext) return [];
  if (ext.valueString) return ext.valueString.split('|');
  return [];
}

function getStringExtension(
  patient: Patient,
  url: string,
): string {
  return patient.extension?.find((e) => e.url === url)?.valueString ?? '';
}

export function patientToClientProfileForm(
  patient: Patient,
): ClientProfileFormState {
  const nickname = findName(patient, 'nickname');
  const official = findName(patient, 'official');
  const phone = findTelecom(patient, 'phone');
  const email = findTelecom(patient, 'email');
  const homeAddress = patient.address?.find((a) => a.use === 'home') ?? patient.address?.[0];
  const langs = (patient.communication ?? [])
    .map((c) => c.language?.text ?? c.language?.coding?.[0]?.display ?? '')
    .filter(Boolean);

  return {
    preferredFirstName: nickname?.given?.[0] ?? '',
    preferredLastName: nickname?.family ?? '',
    legalFirstName: official?.given?.[0] ?? '',
    legalLastName: official?.family ?? '',
    dateOfBirth: patient.birthDate ?? '',
    street: homeAddress?.line?.[0] ?? '',
    city: homeAddress?.city ?? '',
    state: homeAddress?.state ?? '',
    zipCode: homeAddress?.postalCode ?? '',
    phone: phone?.value ?? '',
    email: email?.value ?? '',
    genderIdentity: getStringExtension(patient, PATIENT_EXT.GENDER_IDENTITY),
    lgbtqia: getStringListExtension(patient, PATIENT_EXT.LGBTQIA_IDENTITIES),
    religion: getStringListExtension(patient, PATIENT_EXT.RELIGION_SPIRITUALITY),
    raceEthnicity: getStringListExtension(patient, PATIENT_EXT.RACE_ETHNICITY),
    languages: langs,
  };
}

// ---------------------------------------------------------------------------
// Form → Patient patch (preserves unrelated fields/extensions)
// ---------------------------------------------------------------------------

function upsertName(
  names: HumanName[],
  use: string,
  given: string,
  family: string,
): HumanName[] {
  const next = names.filter((n) => n.use !== use);
  if (given || family) {
    next.push({
      use: use as HumanName['use'],
      given: given ? [given] : undefined,
      family: family || undefined,
      text: [given, family].filter(Boolean).join(' ') || undefined,
    });
  }
  return next;
}

function upsertTelecom(
  telecoms: ContactPoint[],
  system: string,
  value: string,
  use: string,
): ContactPoint[] {
  const next = telecoms.filter((t) => t.system !== system);
  if (value) {
    next.push({ system: system as ContactPoint['system'], value, use: use as ContactPoint['use'] });
  }
  return next;
}

function upsertStringExtension(
  extensions: Extension[],
  url: string,
  value: string,
): Extension[] {
  const next = extensions.filter((e) => e.url !== url);
  if (value) {
    next.push({ url, valueString: value });
  }
  return next;
}

function upsertStringListExtension(
  extensions: Extension[],
  url: string,
  values: string[],
): Extension[] {
  const next = extensions.filter((e) => e.url !== url);
  if (values.length > 0) {
    next.push({ url, valueString: values.join('|') });
  }
  return next;
}

export function clientProfileFormToPatientPatch(
  patient: Patient,
  form: ClientProfileFormState,
): Patient {
  let names = [...(patient.name ?? [])];
  names = upsertName(names, 'nickname', form.preferredFirstName, form.preferredLastName);
  names = upsertName(names, 'official', form.legalFirstName, form.legalLastName);

  let telecoms = [...(patient.telecom ?? [])];
  telecoms = upsertTelecom(telecoms, 'phone', form.phone, 'mobile');
  telecoms = upsertTelecom(telecoms, 'email', form.email, 'home');

  const addresses = [...(patient.address ?? [])];
  const homeIdx = addresses.findIndex((a) => a.use === 'home');
  if (form.street || form.city || form.state || form.zipCode) {
    const existing = homeIdx >= 0 ? addresses[homeIdx] : {};
    const updated: Address = {
      ...existing,
      use: 'home',
      line: form.street ? [form.street, ...(existing.line?.slice(1) ?? [])] : existing.line?.slice(1),
      city: form.city || undefined,
      state: form.state || undefined,
      postalCode: form.zipCode || undefined,
    };
    if (homeIdx >= 0) {
      addresses[homeIdx] = updated;
    } else {
      addresses.push(updated);
    }
  } else if (homeIdx >= 0) {
    addresses[homeIdx] = {
      ...addresses[homeIdx],
      line: addresses[homeIdx].line?.slice(1),
      city: undefined,
      state: undefined,
      postalCode: undefined,
    };
  }

  const communication: PatientCommunication[] = form.languages.map((lang) => ({
    language: { text: lang },
  }));

  let extensions = [...(patient.extension ?? [])];
  extensions = upsertStringExtension(extensions, PATIENT_EXT.GENDER_IDENTITY, form.genderIdentity);
  extensions = upsertStringListExtension(extensions, PATIENT_EXT.LGBTQIA_IDENTITIES, form.lgbtqia);
  extensions = upsertStringListExtension(extensions, PATIENT_EXT.RELIGION_SPIRITUALITY, form.religion);
  extensions = upsertStringListExtension(extensions, PATIENT_EXT.RACE_ETHNICITY, form.raceEthnicity);

  return {
    ...patient,
    name: names.length > 0 ? names : undefined,
    telecom: telecoms.length > 0 ? telecoms : undefined,
    address: addresses.length > 0 ? addresses : undefined,
    birthDate: form.dateOfBirth || undefined,
    communication: communication.length > 0 ? communication : undefined,
    extension: extensions.length > 0 ? extensions : undefined,
  };
}

// ---------------------------------------------------------------------------
// Patient header helpers (from Patient resource directly)
// ---------------------------------------------------------------------------

export function getPatientOfficialName(patient: Patient): string {
  const n = findName(patient, 'official');
  if (n) return [n.given?.[0], n.family].filter(Boolean).join(' ');
  const any = patient.name?.[0];
  return any?.text ?? [any?.given?.[0], any?.family].filter(Boolean).join(' ') ?? '';
}

export function getPatientPreferredName(patient: Patient): string {
  const n = findName(patient, 'nickname');
  if (n) return [n.given?.[0], n.family].filter(Boolean).join(' ');
  return getPatientOfficialName(patient);
}

export function getPatientInitials(patient: Patient): string {
  const nick = findName(patient, 'nickname');
  const src = nick ?? findName(patient, 'official') ?? patient.name?.[0];
  if (!src) return '?';
  const g = src.given?.[0]?.charAt(0) ?? '';
  const f = src.family?.charAt(0) ?? '';
  return `${g}${f}`.toUpperCase() || '?';
}

export function getPatientEmail(patient: Patient): string {
  return findTelecom(patient, 'email')?.value ?? '';
}

export function getPatientPhone(patient: Patient): string {
  return findTelecom(patient, 'phone')?.value ?? '';
}

// ---------------------------------------------------------------------------
// Patient.contact ↔ Contact form adapters
// ---------------------------------------------------------------------------

const FORM_RELATIONSHIP_TYPES = new Set<string>(CONTACT_RELATIONSHIP_TYPES);
const GUARANTOR_CODE = 'GUAR';

function isEmergencyRelationshipCoding(coding: Coding): boolean {
  if (coding.code !== EMERGENCY_CONTACT_CODE) return false;
  return !coding.system || coding.system === EMERGENCY_CONTACT_RELATIONSHIP_SYSTEM;
}

function isGuarantorConcept(cc: CodeableConcept): boolean {
  if (cc.text?.trim().toLowerCase() === 'guarantor') return true;
  return (cc.coding ?? []).some(
    (c) => c.code === GUARANTOR_CODE || c.display?.trim().toLowerCase() === 'guarantor',
  );
}

function coerceRelationshipType(raw: string): ContactRelationshipType {
  if (FORM_RELATIONSHIP_TYPES.has(raw)) return raw as ContactRelationshipType;
  if (raw === 'Father' || raw === 'Mother') return 'Parent';
  return 'Other';
}

/** Skip emergency/guarantor concepts so family relationship is not overwritten by role flags. */
export function resolveContactRelationship(
  relationships: CodeableConcept[] | undefined,
): { relationshipType: ContactRelationshipType; role?: string; isGuarantor: boolean } {
  const list = relationships ?? [];
  const isGuarantor = list.some(isGuarantorConcept);

  for (const cc of list) {
    if (isGuarantorConcept(cc)) continue;
    for (const coding of cc.coding ?? []) {
      if (isEmergencyRelationshipCoding(coding)) continue;
      const fromCode = coding.code ? V3_CODE_TO_RELATIONSHIP_LABEL[coding.code] : undefined;
      const raw = fromCode ?? coding.display?.trim();
      if (!raw) continue;
      const relationshipType = coerceRelationshipType(raw);
      const leftover = relationshipType === 'Other' ? (cc.text?.trim() || raw) : cc.text?.trim();
      const role =
        relationshipType === 'Other' || relationshipType === 'Physician' ? leftover || undefined : undefined;
      return { relationshipType, role, isGuarantor };
    }
    const text = cc.text?.trim();
    if (text) {
      const relationshipType = coerceRelationshipType(text);
      return {
        relationshipType,
        role: relationshipType === 'Other' ? text : undefined,
        isGuarantor,
      };
    }
  }

  return { relationshipType: 'Other', isGuarantor };
}

export function contactKindFromFlags(opts: {
  source: ContactSource;
  isGuarantor?: boolean;
}): ContactKind {
  if (opts.isGuarantor) return 'guarantor';
  if (opts.source === 'related-person') return 'related-person';
  return 'emergency-contact';
}

function telecomFromContact(contact: Omit<Contact, 'id'>): ContactPoint[] {
  const telecom: ContactPoint[] = [];
  for (const p of contact.phones ?? []) {
    if (p.value) {
      telecom.push({
        system: 'phone',
        value: p.value,
        use: (p.type?.toLowerCase() ?? 'mobile') as ContactPoint['use'],
      });
    }
  }
  for (const e of contact.emails ?? []) {
    if (e) telecom.push({ system: 'email', value: e, use: 'home' });
  }
  return telecom;
}

function addressFromContact(contact: Omit<Contact, 'id'>): Address | undefined {
  if (!contact.street && !contact.city && !contact.state && !contact.zip) return undefined;
  return {
    line: contact.street ? [contact.street] : undefined,
    city: contact.city,
    state: contact.state,
    postalCode: contact.zip,
  };
}

function buildRelationshipConcepts(contact: Omit<Contact, 'id'>): CodeableConcept[] {
  const relationship: CodeableConcept[] = [];
  const family = relationshipCodingForType(contact.relationshipType);
  if (family) {
    relationship.push({
      coding: [family],
      text: contact.role?.trim() || undefined,
    });
  }
  if (contact.contactKind === 'guarantor') {
    relationship.push({
      text: 'Guarantor',
      coding: [{ code: GUARANTOR_CODE, display: 'Guarantor' }],
    });
  }
  if (contact.source === 'patient-contact' && contact.isEmergencyContact) {
    relationship.push({
      coding: [
        {
          system: EMERGENCY_CONTACT_RELATIONSHIP_SYSTEM,
          code: EMERGENCY_CONTACT_CODE,
          display: 'Emergency Contact',
        },
      ],
    });
  }
  return relationship;
}

function phonesAndEmailsFromTelecom(telecom: ContactPoint[] | undefined): {
  phones?: ContactPhone[];
  emails?: string[];
} {
  const phones: ContactPhone[] = (telecom ?? [])
    .filter((t) => t.system === 'phone')
    .map((t) => ({ value: t.value ?? '', type: t.use ?? 'Mobile' }));
  const emails = (telecom ?? [])
    .filter((t) => t.system === 'email')
    .map((t) => t.value ?? '')
    .filter(Boolean);
  return {
    phones: phones.length > 0 ? phones : undefined,
    emails: emails.length > 0 ? emails : undefined,
  };
}

export function patientContactToContact(
  pc: PatientContact,
  index: number,
): Contact {
  const firstName = pc.name?.given?.[0] ?? '';
  const lastName = pc.name?.family ?? '';
  const resolved = resolveContactRelationship(pc.relationship);
  const isEmergency = (pc.relationship ?? []).some((cc) =>
    (cc.coding ?? []).some(isEmergencyRelationshipCoding),
  );
  const { phones, emails } = phonesAndEmailsFromTelecom(pc.telecom);

  return {
    id: `pc-${index}`,
    source: 'patient-contact',
    contactKind: 'emergency-contact',
    legalFirstName: firstName,
    legalLastName: lastName,
    relationshipType: resolved.relationshipType,
    isEmergencyContact: isEmergency,
    role: resolved.role,
    organization: pc.organization?.display,
    phones,
    emails,
    street: pc.address?.line?.[0],
    city: pc.address?.city,
    state: pc.address?.state,
    zip: pc.address?.postalCode,
  };
}

export function relatedPersonToContact(rp: RelatedPerson): Contact {
  const n = rp.name?.[0];
  const firstName = n?.given?.[0] ?? '';
  const lastName = n?.family ?? '';
  const resolved = resolveContactRelationship(rp.relationship);
  const { phones, emails } = phonesAndEmailsFromTelecom(rp.telecom);
  const contactKind = contactKindFromFlags({ source: 'related-person', isGuarantor: resolved.isGuarantor });

  return {
    id: rp.id ? `rp-${rp.id}` : `rp-${firstName}-${lastName}`,
    source: 'related-person',
    relatedPersonId: rp.id,
    active: rp.active !== false,
    contactKind,
    legalFirstName: firstName,
    legalLastName: lastName,
    relationshipType: resolved.relationshipType,
    isEmergencyContact: false,
    role: resolved.role,
    phones,
    emails,
    street: rp.address?.[0]?.line?.[0],
    city: rp.address?.[0]?.city,
    state: rp.address?.[0]?.state,
    zip: rp.address?.[0]?.postalCode,
  };
}

export function contactFormToPatientContact(
  contact: Omit<Contact, 'id'>,
): PatientContact {
  const relationship = buildRelationshipConcepts({ ...contact, source: 'patient-contact' });
  const telecom = telecomFromContact(contact);
  const address = addressFromContact(contact);

  return {
    relationship: relationship.length > 0 ? relationship : undefined,
    name: {
      given: contact.legalFirstName ? [contact.legalFirstName] : undefined,
      family: contact.legalLastName || undefined,
    },
    telecom: telecom.length > 0 ? telecom : undefined,
    address,
    organization: contact.organization?.trim()
      ? { display: contact.organization.trim() }
      : undefined,
  };
}

// ---------------------------------------------------------------------------
// RelatedPerson ↔ Contact CRUD helpers
// ---------------------------------------------------------------------------

/**
 * Build a new RelatedPerson resource from a Contact form payload.
 * Per PRD, RelatedPerson stores relationship + patient reference.
 * Name/telecom/address are included on the RelatedPerson for search-ability
 * (FHIR spec allows it even though PRD stores demographics on the related
 * person's own Patient login profile for portal users).
 */
export function contactFormToRelatedPerson(
  contact: Omit<Contact, 'id'>,
  patientId: string,
  existingRp?: RelatedPerson,
): RelatedPerson {
  const relationship = buildRelationshipConcepts({ ...contact, source: 'related-person' });
  const telecom = telecomFromContact(contact);
  const address = addressFromContact(contact);

  return {
    ...(existingRp ?? {}),
    resourceType: 'RelatedPerson',
    patient: { reference: `Patient/${patientId}` },
    relationship: relationship.length > 0 ? relationship : undefined,
    name: [
      {
        use: 'official',
        given: contact.legalFirstName ? [contact.legalFirstName] : undefined,
        family: contact.legalLastName || undefined,
      },
    ],
    telecom: telecom.length > 0 ? telecom : undefined,
    address: address ? [{ ...address, use: 'home' }] : existingRp?.address,
    active: true,
  };
}

/**
 * Build a patch to reactivate a RelatedPerson after soft-delete.
 * Preserves all existing fields; only sets active = true.
 */
export function reactivateRelatedPersonPatch(rp: RelatedPerson): RelatedPerson {
  return { ...rp, active: true };
}

/**
 * Build a patch to deactivate a RelatedPerson (soft-delete per PRD).
 * Preserves all existing fields; only sets active = false.
 */
export function deactivateRelatedPersonPatch(rp: RelatedPerson): RelatedPerson {
  return { ...rp, active: false };
}
